/**
 *
 * Mixin
 *
 */

import { mapState, mapGetters, mapActions } from 'vuex';
import _ from 'lodash';
import { Quill } from 'vue2-editor';

import { OSS_TIMER } from '@/constants';
import { swalWarning } from '@/utils/swal2';
import { isAppleSSafari } from '@/utils/browser';

import { HtmlMixin } from './HtmlMixin';
import { SizeClass, SizeStyle } from './quill/formats/size';
import { Imagepreview } from './quill/formats/imagepreview';
import { MathliveBlot } from './quill/formats/mathlive';
import { Timetag } from './quill/formats/timetag';
import MtToolbar from './quill/modules/toolbar';
import ToolbarCustom from './quill/modules/toolbarCustom';
import ImageResize from './quill/modules/quill-image-resize/ImageResize';
import MtSnowTheme from './quill/themes/snow';
import Icons from './quill/ui/icons';
import MtColorPicker from './quill/ui/color-picker';
import MtIconPicker from './quill/ui/icon-picker';
import MtPicker from './quill/ui/picker';

export const EDITOR_TOOL_BAR = [
  [
    {
      size: ['12px', '14px', '16px', '18px', '20px', '22px', '24px'],
    },
  ],
  ['bold', 'underline', 'strike'], // toggled buttons
  /* [
    {
      align: '',
    },
    {
      align: 'center',
    },
    {
      align: 'right',
    },
  ], */
  /* ['blockquote'], */
  [
    {
      color: [],
    },
    {
      background: [],
    },
  ],
  [
    {
      list: 'ordered',
    },
    {
      list: 'bullet',
    },
  ],
  ['image'],
  //['mathlive'],
];

const NOTE_FONT_SIZE_STORAGE_KEY = 'pz-note-editor-font-size';
const NOTE_FONT_SIZE_OPTIONS = ['12px', '14px', '16px', '18px', '20px', '22px', '24px'];
const DEFAULT_NOTE_FONT_SIZE = '14px';

Quill.register(SizeClass, true);
Quill.register(SizeStyle, true);
Quill.register('formats/imagepreview', Imagepreview);
Quill.register('formats/mathlive', MathliveBlot);
Quill.register('formats/timetag', Timetag);
Quill.register('modules/toolbar', MtToolbar, true);
Quill.register('modules/toolbarCustom', ToolbarCustom);
Quill.register('modules/imageResize', ImageResize);
Quill.register('themes/snow', MtSnowTheme, true);
Quill.register('ui/icon', Icons, true);
Quill.register('ui/picker', MtPicker, true);
Quill.register('ui/icon-picker', MtIconPicker, true);
Quill.register('ui/color-picker', MtColorPicker, true);

const Delta = Quill.import('delta');

Quill.prototype.forceEmitTextChange = function () {
  this.emitter.emit(Quill.events.TEXT_CHANGE, new Delta(), this.editor.delta, Quill.sources.USER);
};

Quill.prototype.isBlankLine = function (index) {
  return this.getLine(index)[1] === 0;
};

const quillAMatcher = function (node, delta) {
  return delta.compose(new Delta().retain(delta.length(), { link: null, color: null }));
};

const quillMathliveMatcher = function () {
  return new Delta();
};

const quillImgMatcher = function (node, delta) {
  const src = node.getAttribute('src');
  if (src && src.match(/https?:\/\/[a-z]+\.pzacademy\.com/)) {
    return delta;
  } else {
    return new Delta().insert('\n');
  }
};

let isGuardTextMatched = false;
const quillTextMatcher = function (node, delta) {
  const strNode = node.textContent;
  if (strNode.indexOf('\uFEFF') >= 0) {
    isGuardTextMatched = !isGuardTextMatched;
    let guardDelta = new Delta().insert(strNode.replace('\uFEFF', ''));
    if (!isGuardTextMatched) {
      guardDelta.insert('\n');
    }
    return guardDelta;
  } else if (isGuardTextMatched) {
    return new Delta();
  } else {
    return delta;
  }
};

export const MtEditorMixin = {
  mixins: [HtmlMixin],

  props: {
    editable: {
      type: Boolean,
      default: () => false,
    },

    typeOfEditor: {
      type: String,
      default: () => 'EXERCISE_QA',
    },

    value: {
      type: String,
      default: () => '',
    },
  },

  data: () => {
    return {
      quill: null,
      customToolbar: EDITOR_TOOL_BAR,
      editorSettings: {
        modules: {
          clipboard: {
            matchers: [
              ['A', quillAMatcher],
              ['span.mathlive-blot', quillMathliveMatcher],
              ['IMG', quillImgMatcher],
              [Node.TEXT_NODE, quillTextMatcher],
            ],
          },
          imageResize: {
            modules: ['Resize', 'DisplaySize'],
            overlayStyles: {
              border: '1px dashed rgb(0, 161, 214)',
            },
            handleStyles: {
              height: '13px',
              width: '13px',
              backgroundColor: 'rgb(0, 161, 214)',
              borderRadius: '50%',
              border: '2px solid rgb(255, 255, 255)',
              boxShadow: 'rgb(0 0 0 / 45%) 0px 2px 2px 0px',
              cursor: 'nwse-resize',
            },
            displayStyles: {
              color: 'rgb(102, 102, 102)',
              borderRadius: '3px',
              border: 'none',
              opacity: 1,
            },
          },
        },
      },

      editorContent: '',
      editorContentMaxLength: 2000,

      promiseGetOssPolicy: null,
      itvlGetOssPolicy: null,

      objUploadImg: {},

      //
      prevValue: '',

      noteFontSize: DEFAULT_NOTE_FONT_SIZE,
    };
  },

  computed: {
    ...mapState('Mixins/MtEditor', {
      ms_OSSConfig: 'OSSConfig',
    }),

    ...mapGetters({
      mg_userInfo: 'userInfo',
    }),

    c_isLogin() {
      return Boolean(this.mg_userInfo.studentId);
    },

    defaultSpeed: function () {
      return this.mg_userInfo && this.mg_userInfo.defaultSpeed;
    },

    c_editorContent() {
      let editorContent = this.editorContent;
      for (let k in this.objUploadImg) {
        while (-1 !== editorContent.indexOf(k)) {
          editorContent = editorContent.replace(k, this.objUploadImg[k].realUrl);
        }
      }
      editorContent = this.safeHtmlFilterInput(editorContent);
      editorContent = MathliveBlot.replaceHtmlString(editorContent);
      editorContent = Timetag.replaceHtmlString(editorContent);
      return editorContent;
    },

    c_ossHost() {
      if (this.ms_OSSConfig.host) {
        return this._fixProtocol(this.ms_OSSConfig.host);
      } else {
        return '';
      }
    },

    c_isQaEditor() {
      return -1 !== this.typeOfEditor.indexOf('QA');
    },

    c_isNoteEditor() {
      return -1 !== this.typeOfEditor.indexOf('NOTE');
    },

    c_isEditable() {
      return this.c_isQaEditor || this.editable;
    },

    c_isUploadImgImmediately() {
      return this.c_isNoteEditor;
    },

    c_isVideoEditor: function () {
      return -1 !== this.typeOfEditor.indexOf('VIDEO');
    },

    c_isExerciseEditor: function () {
      return -1 !== this.typeOfEditor.indexOf('EXERCISE');
    },
  },

  watch: {
    value(v) {
      if (this.prevValue !== v) {
        this.prevValue = v;
        this.editorContent = v;
      }
    },
  },

  methods: {
    ...mapActions('Mixins/MtEditor', {
      ma_getQaOssPolicy: 'getQaOssPolicy',
      ma_getNoteOssPolicy: 'getNoteOssPolicy',
      ma_uploadQaImg: 'uploadQaImg',
    }),

    _fixProtocol(url) {
      if (!url || 'http' === window.location.protocol) {
        return url;
      } else {
        return url.replace(/^http/, 'https');
      }
    },

    getStoredNoteFontSize() {
      let fontSize = DEFAULT_NOTE_FONT_SIZE;
      try {
        fontSize = window.localStorage.getItem(NOTE_FONT_SIZE_STORAGE_KEY) || fontSize;
      } catch (e) {
        // localStorage may be unavailable in privacy-restricted browsers.
      }
      return NOTE_FONT_SIZE_OPTIONS.indexOf(fontSize) >= 0
        ? fontSize
        : DEFAULT_NOTE_FONT_SIZE;
    },

    saveNoteFontSize(fontSize) {
      if (NOTE_FONT_SIZE_OPTIONS.indexOf(fontSize) < 0) {
        return;
      }
      this.noteFontSize = fontSize;
      try {
        window.localStorage.setItem(NOTE_FONT_SIZE_STORAGE_KEY, fontSize);
      } catch (e) {
        // Keep the preference for the current editor instance when storage is unavailable.
      }
    },

    handleNoteFontSize(fontSize) {
      const size = fontSize || DEFAULT_NOTE_FONT_SIZE;
      this.saveNoteFontSize(size);
      this.quill.format('size', size, Quill.sources.USER);
    },

    setNoteFontSizeControl(fontSize) {
      const toolbar = this.quill.getModule('toolbar');
      const select = toolbar.container.querySelector('select.ql-size');
      if (!select) {
        return;
      }
      select.value = fontSize;
      const sizePicker = (this.quill.theme.pickers || []).find(
        (picker) => picker.select === select
      );
      if (sizePicker) {
        sizePicker.update();
      }
    },

    applyStoredNoteFontSize(range) {
      if (!this.c_isNoteEditor || !range || range.length > 0) {
        return;
      }
      const currentFormat = this.quill.getFormat(range);
      if (!currentFormat.size) {
        this.quill.format('size', this.noteFontSize, Quill.sources.SILENT);
        this.setNoteFontSizeControl(this.noteFontSize);
      }
    },

    getOssPolicy() {
      if (
        !this.ms_OSSConfig.expire ||
        parseInt(this.ms_OSSConfig.expire) * 1000 <= new Date().getTime() + 2 * OSS_TIMER
      ) {
        if (this.c_isNoteEditor) {
          this.promiseGetOssPolicy = this.ma_getNoteOssPolicy();
        } else if (this.c_isQaEditor) {
          this.promiseGetOssPolicy = this.ma_getQaOssPolicy();
        }
      }
    },

    _random_string(len) {
      len = len || 32;
      let chars = 'ABCDEFGHJKMNPQRSTWXYZabcdefhijkmnprstwxyz2345678';
      let maxPos = chars.length;
      let pwd = '';
      for (let i = 0; i < len; i++) {
        pwd += chars.charAt(Math.floor(Math.random() * maxPos));
      }
      pwd = pwd + '_' + new Date().getTime();
      return pwd;
    },

    handleImageAdded(file, Editor, cursorLocation, resetUploader) {
      this.readFiles([file], (k) => {
        let index = cursorLocation;
        this._insert(k, index);
        resetUploader();
      });
    },

    handleImageRemoved() {
      //
    },

    /**
     * Handler for drop event to read dropped files from evt.dataTransfer
     * @param {Event} evt
     */
    handleDrop(evt) {
      evt.preventDefault();
      if (evt.dataTransfer && evt.dataTransfer.files && evt.dataTransfer.files.length) {
        if (document.caretRangeFromPoint) {
          const selection = document.getSelection();
          const range = document.caretRangeFromPoint(evt.clientX, evt.clientY);
          if (selection && range) {
            selection.setBaseAndExtent(
              range.startContainer,
              range.startOffset,
              range.startContainer,
              range.startOffset
            );
          }
        }
        this.readFiles(evt.dataTransfer.files, this.insert.bind(this));
      }
    },

    /**
     * Handler for paste event to read pasted files from evt.clipboardData
     * @param {Event} evt
     */
    handlePaste(evt) {
      if (
        evt.clipboardData &&
        evt.clipboardData.items &&
        evt.clipboardData.items.length &&
        -1 !== evt.clipboardData.types.indexOf('Files')
      ) {
        const fileItems = _.filter(evt.clipboardData.items, (item) => 'file' === item.kind);
        this.readFiles(fileItems, (dataUrl) => {
          const selection = this.quill.getSelection();
          if (selection) {
            // we must be in a browser that supports pasting (like Firefox)
            // so it has already been placed into the editor
            this.delete();
          } else {
            // otherwise we wait until after the paste when this.quill.getSelection()
            // will return a valid index
            //setTimeout(() => this.insert(dataUrl), 0);
          }
          setTimeout(() => this.insert(dataUrl), 0);
        });
      }
    },

    _insert(dataUrl, idx) {
      if (this.c_isNoteEditor) {
        const contents = this.quill.getContents();
        const imagepreviews = _.filter(contents.ops, (delta) => {
          return undefined !== delta.insert.imagepreview;
        });
        if (imagepreviews.length >= 10) {
          swalWarning('图片不要超过10张！');
          return false;
        }
      }
      let index = idx;
      if (!this.quill.isBlankLine(index)) {
        this.quill.insertText(index, '\n');
        index++;
      }
      this.quill.updateContents(
        new Delta()
          .retain(index)
          .insert({ imagepreview: dataUrl }, { 'loading-img': this.c_isUploadImgImmediately }),
        Quill.sources.USER
      );
      this.$emit('image-added', {
        allPromiseUploadImg: this._getAllPromiseUploadImg(),
        uploadQaImg: this.uploadQaImg,
      });
      const [leaf] = this.quill.getLeaf(index);
      this.objUploadImg[dataUrl].leaf = leaf;
      this.quill.insertText(index + 1, '\n');
      this.quill.setSelection(index + 2, Quill.sources.USER);
    },

    /**
     * Insert the image into the document at the current cursor position
     * @param {String} dataUrl  The base64-encoded image URI
     */
    insert(dataUrl, focus = false) {
      //const index = (this.quill.getSelection(focus) || {}).index || this.quill.getLength();
      let index = (this.quill.getSelection(focus) || {}).index;
      if (undefined === index) {
        index = this.quill.getLength();
      }
      this._insert(dataUrl, index);
    },

    delete() {
      let index = (this.quill.getSelection() || {}).index || this.quill.getLength();
      this.quill.deleteText(index - 1, 1, Quill.sources.USER);
    },

    _getFileName(file) {
      let ext = '';
      if (file.name) {
        ext = file.name.substring(file.name.lastIndexOf('.') + 1).toLowerCase();
      } else if (file.type) {
        ext = file.type.substring(file.type.lastIndexOf('/') + 1).toLowerCase();
      }
      let name = this._random_string();
      return name + '.' + ext;
    },

    _getRealUrlKey(fileName) {
      let url;
      if (this.c_isNoteEditor) {
        url = 'image/notes/';
      } else {
        url = `${this.ms_OSSConfig.dir}/stu_`;
      }
      url = url + this.mg_userInfo.studentId + '/' + fileName;
      return url;
    },

    _getRealUrl(fileName) {
      return this.c_ossHost + '/' + this._getRealUrlKey(fileName);
    },

    _getAllPromiseUploadImg() {
      let promises = [];
      for (let k in this.objUploadImg) {
        promises.push(this.objUploadImg[k].promise);
      }
      return promises;
    },

    async _readBlobCallback(k, blob, callback) {
      await this.promiseGetOssPolicy;
      const fileName = this._getFileName(blob);
      const realUrl = this._getRealUrl(fileName);
      this.$set(this.objUploadImg, k, {
        realUrl,
        file: blob,
        fileName,
        promise: null,
      });
      if (this.c_isUploadImgImmediately) {
        this._uploadQaImg(k);
      }
      callback(k);
    },

    insertCurrentCapture() {
      const k = window.player.getScreenshotData();
      if ('data:,' === k) {
        return;
      }
      const elVueEditor = this.$refs.vEditor.$el;
      let canvas = elVueEditor.querySelector('canvas.ql-screenshot');
      if (canvas == null) {
        canvas = document.createElement('canvas');
        canvas.classList.add('ql-screenshot');
        canvas.setAttribute('style', 'display:none');
        elVueEditor.appendChild(canvas);
      }
      const image = new Image();
      image.onload = () => {
        const scale = 1;
        const width = image.width * scale;
        const height = image.height * scale;
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(image, 0, 0, width, height);
        //if (this.c_isQaEditor) {
        //  //ctx.textBaseline = 'top'
        //  //ctx.font = '96px serif';
        //  //const x = Math.random() * 0.7 * width + 10
        //  //const y = Math.random() * 0.8 * height + 15
        //  //ctx.strokeText(this.userInfo.nickname, x, y, width - x)
        //  //ctx.strokeText(this.userInfo.mobile, x, y + 100, width - x)
        //  //ctx.textBaseline = 'top';
        //  //ctx.font = 'bold 48px serif';
        //  //ctx.fillStyle = 'rgb(102, 153, 0)';
        //  //const polyvCurrentTime = window.player.j2s_getCurrentTime();
        //  //const objTimeAndStr = Timetag.getObjTimeAndStr({
        //  //  time: polyvCurrentTime,
        //  //  speed: this.defaultSpeed,
        //  //});
        //  //ctx.fillText(objTimeAndStr.textContent, width - 270, height - 60);
        //}
        canvas.toBlob((blob) => {
          this._readBlobCallback(k, blob, (k) => {
            this.insert(k, true);
          });
        });
      };
      image.src = k;
    },

    /**
     * Extract image URIs a list of files from evt.dataTransfer or evt.clipboardData
     * @param {File[]} files  One or more File objects
     * @param {Function} callback  A function to send each data URI to
     */
    readFiles(files, callback) {
      // check each file for an image
      [].forEach.call(files, (file) => {
        if (!file.type.match(/^image\/(gif|jpe?g|a?png|svg|webp|bmp|vnd\.microsoft\.icon)/i)) {
          // file is not an image
          // Note that some file formats such as psd start with image/* but are not readable
          swalWarning('只能上传图片格式文件');
          return;
        }
        if (file.size > 5 * 1024 * 1024) {
          swalWarning('图片大小不能超过5MB');
          return;
        }
        // set up file reader
        const reader = new FileReader();
        reader.onload = (evt) => {
          const k = evt.target.result;
          this._readBlobCallback(k, blob, callback);
        };
        // read the clipboard item or file
        const blob = file.getAsFile ? file.getAsFile() : file;
        if (blob instanceof Blob) {
          reader.readAsDataURL(blob);
        }
      });
    },

    customImageHandler() {
      this.$refs.vEditor.$refs.fileInput.click();
    },

    async _uploadQaImg(k) {
      if (!this.c_isUploadImgImmediately) {
        if (-1 === this.editorContent.indexOf(k)) {
          return;
        }
      }
      let formData = new FormData();
      formData.append('OSSAccessKeyId', this.ms_OSSConfig.accessid);
      formData.append('policy', this.ms_OSSConfig.policy);
      formData.append('Signature', this.ms_OSSConfig.signature);
      formData.append('key', this._getRealUrlKey(this.objUploadImg[k].fileName));
      formData.append('success_action_status', 200);
      formData.append('file', this.objUploadImg[k].file);
      let promise = null;
      try {
        promise = this.ma_uploadQaImg({
          url: this.c_ossHost,
          data: formData,
        });
        this.objUploadImg[k].promise = promise;
        if (this.c_isUploadImgImmediately) {
          await promise;
          const leaf = this.objUploadImg[k].leaf;
          if (leaf) {
            const index = this.quill.getIndex(leaf);
            if (index >= 0) {
              this.quill.updateContents(
                new Delta().retain(index).retain(1, { uploaded: true }),
                Quill.sources.USER
              );
            }
          }
        }
      } catch (e) {
        swalWarning('哎呀，有图片保存失败了');
      }
      return promise;
    },

    uploadQaImg() {
      for (let k in this.objUploadImg) {
        this._uploadQaImg(k);
      }
      return this._getAllPromiseUploadImg();
    },

    insertTimeTag(objTime) {
      let range = this.quill.getSelection(true);
      let index = range.index;
      if (!this.quill.isBlankLine(index)) {
        this.quill.insertText(index, '\n');
        index++;
      }
      this.quill.insertText(index, '\xa0');
      this.quill.insertEmbed(index + 1, 'timetag', objTime, Quill.sources.USER);
      this.quill.insertText(index + 2, '\xa0\n\n');
      this.quill.setSelection(index + 4, Quill.sources.USER);
    },

    insertCurrentTimeTag() {
      const polyvCurrentTime = window.player.j2s_getCurrentTime();
      this.insertTimeTag({
        time: polyvCurrentTime,
        //videoId: this.video.videoId,
        //vpath: vid,
        speed: this.defaultSpeed,
      });
    },

    handleInputEditorContent() {
      if (this.c_editorContent !== this.prevValue) {
        this.prevValue = this.c_editorContent;
        this.$emit('input', this.c_editorContent);
        if (this.c_isNoteEditor) {
          this.emitSave(false);
        }
      }
    },

    emitSave(isSaveImmediately = true) {
      this.$emit('save', {
        isSaveImmediately,
      });
    },
  },

  async created() {
    if (this.value) {
      this.prevValue = this.value;
      this.editorContent = this.value;
    }

    if (this.c_isLogin) {
      this.getOssPolicy();
      this.itvlGetOssPolicy = setInterval(this.getOssPolicy, OSS_TIMER);
    }

    let editorSettingsModules = this.editorSettings.modules;
    editorSettingsModules.toolbar = {
      container: EDITOR_TOOL_BAR,
    };
    let toolbarCustom = [];
    if (this.c_isVideoEditor) {
      if (!isAppleSSafari()) {
        toolbarCustom = [...toolbarCustom, 'capture'];
      }
      toolbarCustom = [...toolbarCustom, 'tag'];
    }
    if (this.c_isNoteEditor) {
      toolbarCustom = [...toolbarCustom, 'save'];
    }
    if (toolbarCustom.length > 0) {
      editorSettingsModules.toolbar.toolbarCustom = toolbarCustom;
      editorSettingsModules.toolbarCustom = true;
    }
  },

  mounted() {
    this.quill = this.$refs.vEditor.quill;
    this.quill.root.addEventListener('drop', this.handleDrop, false);
    this.quill.root.addEventListener('paste', this.handlePaste, false);
    this.quill.root.setAttribute('spellcheck', false);

    let toolbar = this.quill.getModule('toolbar');
    if (this.c_isNoteEditor) {
      this.noteFontSize = this.getStoredNoteFontSize();
      toolbar.addHandler('size', this.handleNoteFontSize);
      this.quill.on('selection-change', this.applyStoredNoteFontSize);
      this.applyStoredNoteFontSize(this.quill.getSelection());
      this.setNoteFontSizeControl(this.noteFontSize);
    }
    if (this.c_isVideoEditor) {
      if (!isAppleSSafari()) {
        toolbar.addCustomHandler('capture', this.insertCurrentCapture);
      }
      toolbar.addCustomHandler('tag', this.insertCurrentTimeTag);
    }
    if (this.c_isNoteEditor) {
      toolbar.addCustomHandler('save', this.emitSave);
    }
  },

  beforeDestroy() {
    clearInterval(this.itvlGetOssPolicy);
    if (this.quill && this.c_isNoteEditor) {
      this.quill.off('selection-change', this.applyStoredNoteFontSize);
    }
    this.quill.root.removeEventListener('drop', this.handleDrop, false);
    this.quill.root.removeEventListener('paste', this.handlePaste, false);
  },
};

export default MtEditorMixin;
